/* Moments Etched v14.9.1 — accessibility behavior hardening */
(() => {
  'use strict';

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    '[contenteditable="true"]',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function isVisible(element) {
    if (!(element instanceof HTMLElement)) return false;
    if (element.hidden || element.getAttribute('aria-hidden') === 'true') return false;
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  function getFocusable(container) {
    return Array.from(container.querySelectorAll(focusableSelector)).filter(isVisible);
  }

  function activeCustomModal() {
    const candidates = [
      ...document.querySelectorAll('.me-quote-modal:not([hidden]) [role="dialog"][aria-modal="true"]'),
      ...document.querySelectorAll('.me-save-auth-modal:not([hidden]) [role="dialog"][aria-modal="true"]'),
      ...document.querySelectorAll('.me-exit-guard:not([hidden]) [role="dialog"][aria-modal="true"]'),
      ...document.querySelectorAll('.me-designer.is-open > [role="dialog"][aria-modal="true"], .me-designer.is-open .me-designer__dialog[role="dialog"]'),
      ...document.querySelectorAll('.me-b2b-dialog[open], .me-pp-gallery__dialog[open]'),
      ...document.querySelectorAll('.me-finished__lightbox:not([hidden])[role="dialog"][aria-modal="true"]'),
      ...document.querySelectorAll('.me-production-lightbox.is-open .me-production-lightbox__dialog[role="dialog"][aria-modal="true"]')
    ].filter(isVisible);
    return candidates.at(-1) || null;
  }

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab') return;
    const modal = activeCustomModal();
    if (!modal) return;

    const focusable = getFocusable(modal);
    if (!focusable.length) {
      event.preventDefault();
      if (!modal.hasAttribute('tabindex')) modal.setAttribute('tabindex', '-1');
      modal.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || !modal.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !modal.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  }, true);

  // Synchronize button-based radio groups with the ARIA radio pattern while
  // preserving aria-pressed for the existing personalizer logic.
  function syncOrderTypeRadios(root = document) {
    root.querySelectorAll('[data-me-order-type] [data-me-order-mode]').forEach((button) => {
      button.setAttribute('role', 'radio');
      button.setAttribute('aria-checked', button.getAttribute('aria-pressed') === 'true' ? 'true' : 'false');
    });
  }

  syncOrderTypeRadios();

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'aria-pressed') {
        const target = mutation.target;
        if (target instanceof HTMLElement && target.matches('[data-me-order-mode]')) {
          target.setAttribute('aria-checked', target.getAttribute('aria-pressed') === 'true' ? 'true' : 'false');
        }
      }
      mutation.addedNodes.forEach((node) => {
        if (node instanceof HTMLElement) syncOrderTypeRadios(node);
      });
    }
  });
  observer.observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-pressed'] });

  // Make skip-link destination programmatically focusable and announce page context.
  const main = document.getElementById('MainContent');
  if (main && !main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');

  // Add an unobtrusive global live region for custom validation messages that
  // would otherwise only be visually updated.
  let announcer = document.getElementById('me-a11y-announcer');
  if (!announcer) {
    announcer = document.createElement('div');
    announcer.id = 'me-a11y-announcer';
    announcer.className = 'me-visually-hidden';
    announcer.setAttribute('role', 'status');
    announcer.setAttribute('aria-live', 'polite');
    announcer.setAttribute('aria-atomic', 'true');
    document.body.appendChild(announcer);
  }

  const watchedStatus = new WeakMap();
  const statusSelectors = [
    '[data-me-designer-cart-status]',
    '[data-me-size-message]',
    '[data-me-size-import-status]',
    '[data-me-size-editor-status]',
    '[data-me-quote-status]',
    '[data-me-b2b-errors]'
  ].join(',');

  const statusObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      const target = mutation.target instanceof Element ? mutation.target.closest(statusSelectors) : mutation.target.parentElement?.closest(statusSelectors);
      if (!target || !isVisible(target)) continue;
      const message = (target.textContent || '').replace(/\s+/g, ' ').trim();
      if (!message || watchedStatus.get(target) === message) continue;
      watchedStatus.set(target, message);
      announcer.textContent = '';
      window.setTimeout(() => { announcer.textContent = message; }, 30);
    }
  });
  statusObserver.observe(document.body, { subtree: true, childList: true, characterData: true });
})();
